/**
 * Mock directory for the recipient composites (no backend): a small GAL with a group and a
 * shared mailbox, personal contacts, recent recipients (fed by sends) and two identities.
 * Ranking follows the platform API roughly (prefix matches first, recent before contacts before GAL).
 */
import type { WebmailApi } from './index';
import type { Address, PersonEntry } from './types';

type PeopleApi = Pick<
  WebmailApi,
  'suggestRecipients' | 'resolveRecipients' | 'removeSuggestion' | 'groupMembers' | 'addressBook' | 'identities' | 'photoUrl'
>;

const delay = <T>(value: T, ms = 60): Promise<T> => new Promise((resolve) => setTimeout(() => resolve(structuredClone(value)), ms));
const key = (email: string): string => `mock-${email.toLowerCase()}`;
const entry = (name: string, email: string, source: PersonEntry['source'], kind: PersonEntry['kind'] = 'person'): PersonEntry => ({ key: key(email), email, name, source, kind });

const GAL: PersonEntry[] = [
  entry('Bob Example', 'bob@example.com', 'gal'),
  entry('Bodo Bauer', 'bodo.bauer@example.com', 'gal'),
  entry('David Dietz', 'david.dietz@example.com', 'gal'),
  entry('Felix Fuchs', 'felix.fuchs@example.com', 'gal'),
  entry('Service desk', 'servicedesk@example.com', 'gal'),
  entry('Team Test', 'team@example.com', 'gal'),
  entry('Carol Example', 'carol@example.com', 'gal'),
  entry('Room 1', 'room1@example.com', 'gal'),
  entry('Projector', 'projector@example.com', 'gal'),
  entry('All staff', 'all-staff@example.com', 'gal', 'group'),
];

const CONTACTS: PersonEntry[] = [entry('Clara Conrad', 'clara.conrad@example.org', 'contact'), entry('Boris Partner', 'boris@partner.example', 'contact')];

const GROUP_MEMBERS: Record<string, string[]> = { 'all-staff@example.com': ['bob@example.com', 'david.dietz@example.com', 'felix.fuchs@example.com'] };

const quality = (q: string, p: PersonEntry): number => {
  const name = (p.name ?? '').toLowerCase();
  const email = p.email.toLowerCase();
  if (name.startsWith(q) || email.startsWith(q)) return 3;
  if (name.split(/\s+/).some((w) => w.startsWith(q))) return 2;
  return name.includes(q) || email.includes(q) ? 1 : 0;
};

export function createMockPeople(me: () => Address): { api: PeopleApi; remember: (used: readonly Address[]) => void } {
  let recent: PersonEntry[] = [entry('Benno Brandt', 'benno.brandt@partner.example', 'recent'), entry('Greta Graf', 'greta.graf@example.net', 'recent'), entry('Eva Engel', 'eva.engel@example.org', 'recent')];
  const everyone = () => [...recent, ...CONTACTS, ...GAL];

  const search = (query: string): PersonEntry[] => {
    const q = query.trim().toLowerCase();
    const seen = new Set<string>();
    const rank = { recent: 0, contact: 1, gal: 2, external: 3 } as const;
    return everyone()
      .map((p) => ({ p, score: quality(q, p) * 10 - rank[p.source] }))
      .filter(({ p, score }) => score > 0 && !seen.has(p.email) && seen.add(p.email))
      .sort((a, b) => b.score - a.score)
      .map(({ p }) => p);
  };

  return {
    remember(used) {
      for (const a of used) {
        if (everyone().some((p) => p.email.toLowerCase() === a.address.toLowerCase())) continue;
        recent = [entry(a.name || a.address, a.address, 'recent'), ...recent];
      }
    },
    api: {
      suggestRecipients: (query) => delay(search(query).slice(0, 10)),
      resolveRecipients: (entries) =>
        delay(
          entries.map((input) => {
            const q = input.trim().toLowerCase();
            const hits = everyone().filter((p) => quality(q, p) >= 2);
            const exact = hits.filter((p) => p.email.toLowerCase() === q || p.name?.toLowerCase() === q);
            const pool = exact.length > 0 ? exact : hits;
            if (pool.length === 1) return { input, status: 'resolved' as const, match: pool[0] ?? null, candidates: [] };
            if (pool.length > 1) return { input, status: 'ambiguous' as const, match: null, candidates: pool.slice(0, 10) };
            return { input, status: 'unresolved' as const, match: null, candidates: [] };
          }),
        ),
      removeSuggestion: (k) => {
        recent = recent.filter((p) => p.key !== k);
        return delay(undefined);
      },
      groupMembers: (k) => {
        const group = GAL.find((p) => p.key === k);
        const members = (group ? (GROUP_MEMBERS[group.email] ?? []) : []).map((email) => GAL.find((p) => p.email === email)).filter((p): p is PersonEntry => p !== undefined);
        return delay(members);
      },
      addressBook: (query, filter, cursor) => {
        const pool = [...CONTACTS, ...GAL]
          .filter((p) => filter === 'all' || p.source === filter)
          .filter((p) => query.trim() === '' || quality(query.trim().toLowerCase(), p) > 0)
          .sort((a, b) => (a.name ?? a.email).localeCompare(b.name ?? b.email));
        const offset = Number(cursor ?? 0);
        const items = pool.slice(offset, offset + 50);
        return delay({ items, total: pool.length, nextCursor: offset + items.length < pool.length ? String(offset + items.length) : null });
      },
      identities: () => {
        const self = me();
        return delay([
          { email: self.address, name: self.name, kind: 'primary' as const },
          { email: 'team@example.com', name: 'Team Test', kind: 'shared' as const },
        ]);
      },
      photoUrl: () => undefined,
    },
  };
}
